import { useSelector } from 'react-redux';

import { Form } from 'antd';

import getUserTimeZone from 'components/InterviewScheduler/utils/getUserTimeZone';

import { selectActiveUserId } from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';

import useErrorCatch from 'hooks/useErrorCatch';
import { useInterviewSlots } from 'hooks/useInterviewSlots';

import { GTMEventLogger } from 'utils/analytics';

import { InterviewDetails } from 'types/interviewSlots';

import { GTMEvents } from 'constants/enums';

export const useInterviewConfirmation = (
  interviewRequest: InterviewDetails,
  onConfirmed: () => void,
  onLoading: (isLoading: boolean) => void
) => {
  const catchError = useErrorCatch();
  const [form] = Form.useForm();
  const meetingLink = Form.useWatch('meetingLink', form);
  const { selectedSlot } = useInterviewSlots();
  const applicationId = useSelector(selectActiveUserId);

  const isValidUrl = (raw: string) => {
    try {
      const url = new URL(raw.trim());
      return (
        (url.protocol === 'http:' || url.protocol === 'https:') &&
        Boolean(url.hostname)
      );
    } catch {
      return false;
    }
  };

  const canConfirmSlot =
    meetingLink?.trim().length > 0 && isValidUrl(meetingLink.trim());

  const handleConfirmSlot = async () => {
    if (!selectedSlot || !interviewRequest) {
      return;
    }

    const payload = {
      setupMeeting: false,
      slot: {
        timeZone: getUserTimeZone(),
        slotList: [selectedSlot]
      },
      meetingLink: meetingLink?.trim()
    };

    try {
      onLoading?.(true);
      await applicationsService.confirmInterview(
        applicationId,
        interviewRequest.id,
        payload
      );
      onConfirmed();

      GTMEventLogger(
        GTMEvents.ApplicationsListViewSidebarInterviewSlotsSelected
      );
    } catch (error) {
      catchError({ error });
    } finally {
      onLoading?.(false);
    }
  };

  return {
    form,
    selectedSlot,
    canConfirmSlot,
    isValidUrl,
    handleConfirmSlot
  };
};
